import { apiClient } from './api';

export interface CorporateCardUploadItem {
  id: number;
  uploadId: number;
  usageDate: string;
  merchant: string;
  amount: number;
  description?: string;
  memo?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CorporateCardUpload {
  id: number;
  year: number;
  month: number;
  employeeId: number;
  sourceFile?: string;
  totalAmount: number;
  itemCount: number;
  status: 'DRAFT' | 'CONFIRMED';
  confirmedAt?: string;
  items: CorporateCardUploadItem[];
  employee?: {
    id: number;
    name: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface UploadStatusResponse {
  total: number;
  confirmed: number;
  draft: number;
  uploads: CorporateCardUpload[];
  employees: {
    id: number;
    name: string;
    status: string;
    amount: number;
  }[];
}

export const corporateCardUploadApi = {
  // 내 업로드 목록 조회
  getMyUploads: async (): Promise<CorporateCardUpload[]> => {
    return apiClient.getList<CorporateCardUpload>('/corporate-card-upload/my');
  },

  // 특정 월의 내 업로드 조회
  getMyUploadByMonth: async (year: number, month: number): Promise<CorporateCardUpload | null> => {
    try {
      return await apiClient.get<CorporateCardUpload>(`/corporate-card-upload/my/${year}/${month}`);
    } catch {
      return null;
    }
  },

  // 특정 업로드 상세 조회
  getById: async (id: number): Promise<CorporateCardUpload> => {
    return apiClient.get<CorporateCardUpload>(`/corporate-card-upload/${id}`);
  },

  // 엑셀 업로드
  upload: async (file: File, year: number, month: number): Promise<{
    success: boolean;
    message: string;
    upload?: CorporateCardUpload;
    multiMonth?: boolean;
    results?: { year: number; month: number; newCount: number; duplicateCount: number }[];
  }> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('year', year.toString());
    formData.append('month', month.toString());

    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/corporate-card-upload/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Upload failed');
    }

    return response.json();
  },

  // 수동 업로드 생성
  createManual: async (year: number, month: number): Promise<{ success: boolean; upload: CorporateCardUpload }> => {
    return apiClient.post('/corporate-card-upload/manual', { year, month });
  },

  // 항목 추가
  addItem: async (uploadId: number, item: Partial<CorporateCardUploadItem>): Promise<CorporateCardUpload> => {
    return apiClient.post(`/corporate-card-upload/${uploadId}/item`, item);
  },

  // 항목 수정
  updateItem: async (itemId: number, item: Partial<CorporateCardUploadItem>): Promise<CorporateCardUploadItem> => {
    return apiClient.put(`/corporate-card-upload/item/${itemId}`, item);
  },

  // 항목 삭제
  deleteItem: async (itemId: number): Promise<void> => {
    return apiClient.delete(`/corporate-card-upload/item/${itemId}`);
  },

  // 확정
  confirm: async (uploadId: number): Promise<{ success: boolean; message: string; upload: CorporateCardUpload }> => {
    return apiClient.post(`/corporate-card-upload/${uploadId}/confirm`, {});
  },

  // 확정 취소
  unconfirm: async (uploadId: number): Promise<{ success: boolean; message: string; upload: CorporateCardUpload }> => {
    return apiClient.post(`/corporate-card-upload/${uploadId}/unconfirm`, {});
  },

  // 업로드 삭제
  delete: async (uploadId: number): Promise<void> => {
    return apiClient.delete(`/corporate-card-upload/${uploadId}`);
  },

  // 관리자용: 업로드 현황 조회
  getUploadStatus: async (year: number, month: number): Promise<UploadStatusResponse> => {
    return apiClient.get(`/corporate-card/admin/upload-status/${year}/${month}`);
  },

  // 관리자용: 취합
  consolidate: async (year: number, month: number): Promise<{ success: boolean; message: string; warning?: string; usage: any }> => {
    return apiClient.post(`/corporate-card/admin/consolidate/${year}/${month}`, {});
  },

  // 엑셀 양식 다운로드
  downloadTemplate: async (): Promise<void> => {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/corporate-card-upload/template`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('양식 다운로드에 실패했습니다.');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = '법인카드_사용내역_양식.xlsx';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  },
};
